import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, Tabs, InputNumber, Switch, Slider, Input, Select, Tag,
  Tooltip, Button, Space, message, Empty, Spin, Divider, Typography,
} from 'antd';
import {
  InfoCircleOutlined, ReloadOutlined,
  DownloadOutlined, UploadOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import agentConfigService from '../services/agentConfigService';

const { TextArea } = Input;
const { Text } = Typography;

/* ───────────────────────── Field renderer ───────────────────────── */
function FieldRow({ field, value, onChange, isAdmin, hasUserOverride, hasSystemOverride }) {
  const userOnly = field.levels.includes('user') && !field.levels.includes('system');
  const adminOnly = field.levels.includes('system') && !field.levels.includes('user');
  const disabled = adminOnly && !isAdmin;

  let control;
  switch (field.type) {
    case 'bool':
      control = (
        <Switch
          checked={!!value}
          disabled={disabled}
          onChange={(v) => onChange(field.key, v)}
        />
      );
      break;
    case 'int':
      control = (
        <InputNumber
          value={value ?? field.default}
          min={field.min ?? undefined}
          max={field.max ?? undefined}
          step={1}
          disabled={disabled}
          style={{ width: 160 }}
          onChange={(v) => onChange(field.key, v)}
        />
      );
      break;
    case 'float':
      // Use Slider when both min and max are present and span is small
      if (field.min !== null && field.max !== null && (field.max - field.min) <= 1) {
        control = (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: 320 }}>
            <Slider
              min={field.min}
              max={field.max}
              step={0.01}
              value={typeof value === 'number' ? value : field.default}
              disabled={disabled}
              onChange={(v) => onChange(field.key, v)}
              style={{ flex: 1 }}
            />
            <InputNumber
              value={value ?? field.default}
              min={field.min}
              max={field.max}
              step={0.01}
              disabled={disabled}
              style={{ width: 90 }}
              onChange={(v) => onChange(field.key, v)}
            />
          </div>
        );
      } else {
        control = (
          <InputNumber
            value={value ?? field.default}
            min={field.min ?? undefined}
            max={field.max ?? undefined}
            step={0.01}
            disabled={disabled}
            style={{ width: 160 }}
            onChange={(v) => onChange(field.key, v)}
          />
        );
      }
      break;
    case 'string':
      control = (
        <Input
          value={value ?? ''}
          disabled={disabled}
          style={{ width: 320 }}
          onChange={(e) => onChange(field.key, e.target.value)}
        />
      );
      break;
    case 'text':
      control = (
        <TextArea
          value={value ?? ''}
          rows={4}
          disabled={disabled}
          style={{ width: '100%' }}
          onChange={(e) => onChange(field.key, e.target.value)}
        />
      );
      break;
    case 'enum': {
      const options = (field.enum_options || field.enum || []).map((opt) =>
        typeof opt === 'string' ? { value: opt, label: opt } : opt
      );
      control = (
        <Select
          value={value ?? null}
          options={[{ value: null, label: '(use default)' }, ...options]}
          allowClear
          disabled={disabled}
          style={{ width: 320 }}
          onChange={(v) => onChange(field.key, v ?? null)}
        />
      );
      break;
    }
    case 'array_string':
      control = (
        <Select
          mode="tags"
          value={Array.isArray(value) ? value : []}
          tokenSeparators={[',']}
          disabled={disabled}
          style={{ width: 420 }}
          onChange={(v) => onChange(field.key, v)}
        />
      );
      break;
    default:
      control = <Text type="warning">Unknown field type: {field.type}</Text>;
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        padding: '10px 0',
        borderBottom: '1px solid var(--ant-color-split, #f0f0f0)',
      }}
    >
      <div style={{ flex: '0 0 320px' }}>
        <Space size={6} wrap>
          <Text strong style={{ fontFamily: 'monospace', fontSize: 13 }}>{field.key}</Text>
          {hasUserOverride && <Tag color="blue">user</Tag>}
          {hasSystemOverride && <Tag color="purple">system</Tag>}
          {userOnly && <Tag>user-only</Tag>}
          {adminOnly && <Tag color="gold">admin</Tag>}
          <Tooltip title={field.description}>
            <InfoCircleOutlined style={{ color: '#888' }} />
          </Tooltip>
        </Space>
        <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
          {field.description}
          {field.min !== null && field.max !== null && (
            <span style={{ marginLeft: 6 }}>
              (range {field.min}–{field.max})
            </span>
          )}
        </div>
      </div>
      <div style={{ flex: 1 }}>{control}</div>
    </div>
  );
}

/* ───────────────────────── Main dialog ──────────────────────────── */

const AgentConfigDialog = ({ visible, onClose, onSaved }) => {
  const { userEmail, isAdmin } = useAuth();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schema, setSchema] = useState({ fields: [], enum_sources: {} });
  const [effective, setEffective] = useState({});
  const [systemOverrides, setSystemOverrides] = useState({});
  const [userOverrides, setUserOverrides] = useState({});

  /* drafts hold pending edits; on save, we diff against effective and
     submit only changed keys to the appropriate level. */
  const [draft, setDraft] = useState({});

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, c] = await Promise.all([
          agentConfigService.getSchema(),
          agentConfigService.getConfig(userEmail),
        ]);
        if (cancelled) return;
        setSchema(s);
        setEffective(c.effective || {});
        setSystemOverrides(c.system_overrides || {});
        setUserOverrides(c.user_overrides || {});
        setDraft({ ...(c.effective || {}) });
      } catch (e) {
        message.error('Failed to load agent config');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [visible, userEmail]);

  /* Inject dynamic enum options from enum_sources */
  const fields = useMemo(() => {
    const sources = schema.enum_sources || {};
    return (schema.fields || []).map((f) => {
      if (f.type === 'enum' && f.enum_source && Array.isArray(sources[f.enum_source])) {
        return { ...f, enum_options: sources[f.enum_source].map((m) => ({ value: m.key, label: m.label })) };
      }
      return f;
    });
  }, [schema]);

  /* Group by section for tabs */
  const fieldsBySection = useMemo(() => {
    const map = new Map();
    for (const f of fields) {
      if (!map.has(f.section)) map.set(f.section, []);
      map.get(f.section).push(f);
    }
    return Array.from(map.entries());
  }, [fields]);

  const handleChange = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const collectChanges = () => {
    /* Diff draft vs effective. Bucket each changed key by the level
       the current user is allowed to write it at:
         - admin: writes to 'system' for all keys
         - non-admin: writes only allowlisted keys to 'user'
       Returns { systemUpdates, userUpdates, ignored }. */
    const systemUpdates = {};
    const userUpdates = {};
    const ignored = [];
    for (const f of fields) {
      const a = draft[f.key];
      const b = effective[f.key];
      const sameJSON = JSON.stringify(a) === JSON.stringify(b);
      if (sameJSON) continue;
      if (isAdmin) {
        systemUpdates[f.key] = a;
      } else if (f.levels.includes('user')) {
        userUpdates[f.key] = a;
      } else {
        ignored.push(f.key);
      }
    }
    return { systemUpdates, userUpdates, ignored };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { systemUpdates, userUpdates, ignored } = collectChanges();
      const tasks = [];
      if (Object.keys(systemUpdates).length) {
        tasks.push(agentConfigService.putSystemConfig(userEmail, systemUpdates));
      }
      if (Object.keys(userUpdates).length) {
        tasks.push(agentConfigService.putUserConfig(userEmail, userUpdates));
      }
      if (!tasks.length) {
        message.info('No changes to save');
        return;
      }
      const results = await Promise.all(tasks);
      const rejected = results.flatMap((r) => Object.keys(r.rejected || {}));
      if (rejected.length) {
        message.warning(`Saved with ${rejected.length} rejected key(s): ${rejected.join(', ')}`);
      } else {
        message.success('Configuration saved');
      }
      if (ignored.length) {
        message.warning(`Ignored ${ignored.length} admin-only key(s)`);
      }
      // Reload to surface server-coerced values + new override badges
      const c = await agentConfigService.getConfig(userEmail);
      setEffective(c.effective || {});
      setSystemOverrides(c.system_overrides || {});
      setUserOverrides(c.user_overrides || {});
      setDraft({ ...(c.effective || {}) });
      if (onSaved) onSaved(c.effective);
    } catch (e) {
      message.error(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    Modal.confirm({
      title: 'Reset all overrides?',
      content: isAdmin
        ? 'This removes every system-level and your user-level override for the data agent.'
        : 'This removes your user-level overrides for the data agent (system overrides remain).',
      okText: 'Reset',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          if (isAdmin) {
            await agentConfigService.resetConfig(userEmail, true, 'system', null, []);
          }
          if (userEmail) {
            await agentConfigService.resetConfig(userEmail, !!isAdmin, 'user', userEmail, []);
          }
          message.success('Overrides cleared');
          const c = await agentConfigService.getConfig(userEmail);
          setEffective(c.effective || {});
          setSystemOverrides(c.system_overrides || {});
          setUserOverrides(c.user_overrides || {});
          setDraft({ ...(c.effective || {}) });
        } catch (e) {
          message.error(`Reset failed: ${e.message || e}`);
        }
      },
    });
  };

  const handleExport = () => {
    const payload = {
      effective: draft,
      system_overrides: systemOverrides,
      user_overrides: userOverrides,
      exported_at: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `data-agent-config-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (file) => {
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const next = payload.effective || payload;
      setDraft({ ...draft, ...next });
      message.success('Imported into draft. Click Save to persist.');
    } catch (e) {
      message.error('Import failed: invalid JSON');
    }
  };

  return (
    <Modal
      open={visible}
      title="Data Agent Configuration"
      width={920}
      onCancel={onClose}
      destroyOnClose
      footer={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleResetAll} danger>
              Reset overrides
            </Button>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>Export</Button>
            <Button
              icon={<UploadOutlined />}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'application/json';
                input.onchange = (e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImport(f);
                };
                input.click();
              }}
            >
              Import
            </Button>
          </Space>
          <Space>
            <Button onClick={onClose}>Close</Button>
            <Button type="primary" loading={saving} onClick={handleSave}>Save</Button>
          </Space>
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : !fieldsBySection.length ? (
        <Empty description="No config fields available" />
      ) : (
        <>
          <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
            Effective config = user override → system override → built-in default.{' '}
            {isAdmin
              ? 'You are an admin: changes save to the system level for everyone.'
              : 'You can only edit user-level keys (tagged "user-only"); admin-only fields are read-only.'}
          </div>
          <Tabs
            items={fieldsBySection.map(([section, sectionFields]) => ({
              key: section,
              label: section,
              children: sectionFields.map((f) => (
                <FieldRow
                  key={f.key}
                  field={f}
                  value={draft[f.key]}
                  onChange={handleChange}
                  isAdmin={isAdmin}
                  hasUserOverride={Object.prototype.hasOwnProperty.call(userOverrides, f.key)}
                  hasSystemOverride={Object.prototype.hasOwnProperty.call(systemOverrides, f.key)}
                />
              )),
            }))}
          />
          <Divider style={{ margin: '8px 0' }} />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Tip: Export current draft to JSON, tweak offline, and re-import to A/B test config sets.
          </Text>
        </>
      )}
    </Modal>
  );
};

export default AgentConfigDialog;
